'use client'

import { useState } from 'react'
import { DashboardLayout } from '@/components/dashboard-layout'
import { Badge } from '@/components/badge'
import { CheckCircle, Plus, Trash2 } from 'lucide-react'

interface PortForwarding {
  honeypot: string
  publicIp: string
  publicPort: string
  internalIp: string
  internalPort: string
}

const portForwardings: PortForwarding[] = [
  {
    honeypot: 'Cowrie SSH',
    publicIp: '45.32.11.90',
    publicPort: '22',
    internalIp: '192.168.1.10',
    internalPort: '2222',
  },
  {
    honeypot: 'Dionaea SMB',
    publicIp: '45.32.11.90',
    publicPort: '445',
    internalIp: '192.168.1.11',
    internalPort: '445',
  },
  {
    honeypot: 'Log4Pot Web',
    publicIp: '104.1.22.8',
    publicPort: '8080',
    internalIp: '10.0.1.5',
    internalPort: '8080',
  },
]

export default function NetworkControlPage() {
  const [activeTab, setActiveTab] = useState<'rules' | 'config'>('rules')

  return (
    <DashboardLayout
      title="Network Control Layer"
      subtitle="Configure ingress ports, NAT rules, and outbound isolation policies."
      actions={
        <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2">
          <Plus size={18} />
          + Add Port Rule
        </button>
      }
    >
      <div className="space-y-8">
        {/* Port Forwarding & Config Tabs */}
        <div className="bg-card border border-border rounded-lg">
          {/* Tab Navigation */}
          <div className="border-b border-border flex">
            <button
              onClick={() => setActiveTab('rules')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'rules'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Active Port Forwarding (NAT)
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'config'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Config
            </button>
          </div>

          {/* Rules Tab */}
          {activeTab === 'rules' && (
            <div className="p-6">
              <div className="space-y-4">
                {portForwardings.map((rule, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 border border-border rounded-lg bg-secondary/20">
                    <div className="flex items-center gap-6 flex-1">
                      <div className="min-w-32">
                        <p className="text-xs text-muted-foreground mb-1">HONEYPOT</p>
                        <p className="text-sm font-semibold text-foreground">{rule.honeypot}</p>
                      </div>
                      <div className="hidden md:block">
                        <p className="text-xs text-muted-foreground mb-1">PUBLIC ENDPOINT</p>
                        <p className="text-sm font-mono text-foreground">{rule.publicIp}:{rule.publicPort}</p>
                      </div>
                      <div className="hidden lg:block">
                        <p className="text-xs text-muted-foreground mb-1">INTERNAL TARGET</p>
                        <p className="text-sm font-mono text-foreground">{rule.internalIp}:{rule.internalPort}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 hover:bg-secondary rounded transition-colors">
                        <Trash2 size={16} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="mt-6 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-secondary/20 transition-colors font-medium">
                + Add Port Forwarding Rule
              </button>
            </div>
          )}

          {/* Config Tab */}
          {activeTab === 'config' && (
            <div className="p-6">
              <div className="space-y-6">
                {/* NAT Rules Summary */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-4 uppercase">NAT Rules Summary</h4>
                  <div className="space-y-2 bg-secondary/20 rounded-lg p-4">
                    {portForwardings.map((rule, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{rule.honeypot}</span>
                        <span className="text-muted-foreground font-mono">{rule.publicIp}:{rule.publicPort} → {rule.internalIp}:{rule.internalPort}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pending Changes */}
                <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <p className="text-sm font-semibold text-foreground">Pending Changes: 0</p>
                  </div>
                  <p className="text-xs text-muted-foreground">No configuration changes pending application.</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity">
                    Apply Changes
                  </button>
                  <button className="px-6 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-secondary/20 transition-colors">
                    Discard Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Global Network Standards */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-6">Global Network Standards</h3>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-secondary/20 rounded-lg">
              <CheckCircle size={20} className="text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Automatic port conflict detection enabled.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-secondary/20 rounded-lg">
              <CheckCircle size={20} className="text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Outbound bandwidth limited to 1MB/s per instance.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-secondary/20 rounded-lg">
              <CheckCircle size={20} className="text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">ICMP responses enabled for stealth.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-secondary/20 rounded-lg">
              <CheckCircle size={20} className="text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">DHCP overlay enabled for Bridge mode.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Firewall Rollback */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Firewall Rollback</h3>
              <p className="text-sm text-muted-foreground max-w-lg">
                Configuration changes are stored in local state. If the agent loses connection for 30s, it will automatically roll back to the last known-good firewall configuration.
              </p>
            </div>
            <button className="px-4 py-2 border border-border text-foreground rounded-lg hover:bg-secondary/20 transition-colors font-medium whitespace-nowrap">
              Test Connectivity
            </button>
          </div>
        </div>

        {/* Outbound Isolation Policies */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-6">Outbound Isolation Policies</h3>

          <div className="space-y-4">
            <div className="p-4 border-2 border-primary/50 rounded-lg bg-primary/5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-foreground">Standard Restricted (Default)</span>
                <Badge variant="success">ACTIVE</Badge>
              </div>
              <p className="text-sm text-muted-foreground">Allows internal responses only. Blocks all new outbound connections.</p>
            </div>

            <div className="p-4 border border-border rounded-lg hover:border-primary/50 transition-colors cursor-pointer">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-foreground">Full Airgap</span>
                <span className="text-xs text-muted-foreground">Apply</span>
              </div>
              <p className="text-sm text-muted-foreground">Complete network isolation. No traffic enters or leaves the container.</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
